import React, { useState, useEffect } from 'react';
import { Typography, Box, Grid, Button, TextField, Chip } from '@mui/material';
import { AccountBalanceWallet } from '@mui/icons-material';
import { useApiData } from '../hooks/useApiData';
import { API_CREDITS_PAGE_GREEN_COLOR, COLOR_PRIMARY_1 } from 'src/utils/colors';
import CustomBasicCard from 'src/App/Dashboards/Market/Components/Cards/CustomBasicCard';
import { LoadingOverlay, RefreshButton, CreditsSkeleton } from '../Components';
import { getStripeCheckoutUrl, validatePayment } from '../api';
import AutohideSnackbar from 'src/App/Dashboards/Market/Components/Alerts/AutohideSnackbar';

const MINIMUM_PURCHASE_AMOUNT = 10;

const ApiCreditsPage = () => {
  const { apiKeyData, loading, refreshing, refreshData } = useApiData();
  const [selectedAmount, setSelectedAmount] = useState('100');
  const [customAmount, setCustomAmount] = useState('100');
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [snackbarType, setSnackbarType] = useState<'success' | 'error' | 'info'>('success');

  const predefinedAmounts = ['25', '100', '500', '1000']; // All amounts are above minimum

  // Check for session ID in URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (sessionId && sessionId !== 'cancel') {
      // Call validation API with sessionId
      validatePayment(sessionId)
        .then(isSuccess => {
          if (isSuccess) {
            setSnackbarMessage('Payment successful! Credits have been added to your account.');
            setSnackbarType('success');
            refreshData();
          } else {
            setSnackbarMessage('Payment validation failed. Please try again.');
            setSnackbarType('error');
          }
        })
        .catch(error => {
          setSnackbarMessage('Payment validation failed. Please try again.');
          setSnackbarType('error');
        });

      // Clean up URL by removing the session_id parameter
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    } else if (sessionId === 'cancel') {
      setSnackbarMessage('Payment was cancelled');
      setSnackbarType('info');

      // Clean up URL by removing the session_id parameter
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [refreshData]);

  const handleAmountSelect = (amount: string) => {
    setSelectedAmount(amount);
    setCustomAmount(amount);
  };

  const handleCustomAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setCustomAmount(value);
    setSelectedAmount(value);
  };

  const handleBuyCredits = async () => {
    const amount = parseInt(customAmount);

    // Validate minimum amount
    if (amount < MINIMUM_PURCHASE_AMOUNT) {
      setSnackbarMessage(`Minimum purchase amount is $${MINIMUM_PURCHASE_AMOUNT}`);
      setSnackbarType('error');
      return;
    }

    try {
      setIsLoadingCheckout(true);
      const stripeCheckoutUrl = await getStripeCheckoutUrl(amount);
      window.open(stripeCheckoutUrl, '_self');
    } catch (error) {
      setSnackbarMessage(`Error buying credits: ${error}`);
      setSnackbarType('error');
    } finally {
      setIsLoadingCheckout(false);
    }
  };

  if (loading) {
    return <CreditsSkeleton />;
  }

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <RefreshButton onRefresh={refreshData} loading={refreshing} />
      </Box>

      <Grid container spacing={3}>
        {/* Balance Card */}
        <Grid item xs={12} md={6}>
          <LoadingOverlay loading={refreshing}>
            <CustomBasicCard
              title="Balance"
              icon={<AccountBalanceWallet />}
              content={
                <Grid item xs={12}>
                  <Typography
                    variant="h1"
                    sx={{ fontWeight: 700, color: API_CREDITS_PAGE_GREEN_COLOR, fontSize: '4rem' }}
                  >
                    ${apiKeyData?.credits_remaining || 0}.00
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mt: 1,
                    }}
                  >
                    <Chip
                      label={apiKeyData?.status === 'active' ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        backgroundColor: API_CREDITS_PAGE_GREEN_COLOR,
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    />
                  </Box>
                </Grid>
              }
            />
          </LoadingOverlay>
        </Grid>

        {/* Add Credits Card */}
        <Grid item xs={12} md={6}>
          <LoadingOverlay loading={isLoadingCheckout}>
            <CustomBasicCard
              title="Add Credits"
              icon={<AccountBalanceWallet />}
              content={
                <Grid item xs={12}>
                  {/* Select Amount Section */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ color: 'grey.600', mb: 2 }}>
                      Select amount
                    </Typography>
                    <Grid container spacing={1}>
                      {predefinedAmounts.map(amount => (
                        <Grid item xs={6} key={amount}>
                          <Button
                            variant={selectedAmount === amount ? 'contained' : 'outlined'}
                            fullWidth
                            disabled={isLoadingCheckout}
                            onClick={() => handleAmountSelect(amount)}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 500,
                              borderColor: 'grey.300',
                              color: selectedAmount === amount ? 'white' : 'grey.700',
                              '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor:
                                  selectedAmount === amount ? 'primary.main' : 'primary.50',
                              },
                              '&:disabled': {
                                opacity: 0.6,
                              },
                            }}
                          >
                            ${amount}
                          </Button>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>

                  {/* Custom Amount Section */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ color: 'grey.600', mb: 1 }}>
                      Custom Amount
                    </Typography>
                    <TextField
                      fullWidth
                      disabled={isLoadingCheckout}
                      value={customAmount}
                      onChange={handleCustomAmountChange}
                      helperText={`Minimum amount: $${MINIMUM_PURCHASE_AMOUNT}`}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'grey.300',
                          },
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: '12px',
                          color: 'grey.600',
                        },
                      }}
                    />
                  </Box>

                  {/* Buy Credits Button */}
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleBuyCredits}
                    disabled={isLoadingCheckout}
                    sx={{
                      backgroundColor: COLOR_PRIMARY_1,
                      color: 'white',
                      fontWeight: 600,
                      py: 1.5,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: COLOR_PRIMARY_1,
                      },
                      '&:disabled': {
                        backgroundColor: COLOR_PRIMARY_1,
                        opacity: 0.7,
                      },
                    }}
                  >
                    {isLoadingCheckout ? 'Processing...' : `Buy $${selectedAmount} Credits`}
                  </Button>
                </Grid>
              }
            />
          </LoadingOverlay>
        </Grid>
      </Grid>

      {snackbarMessage && (
        <AutohideSnackbar
          message={snackbarMessage}
          type={snackbarType}
          handleOnCloseSideEffect={() => setSnackbarMessage(null)}
        />
      )}
    </Box>
  );
};

export default ApiCreditsPage;
